/**
 * SOLID Review
 * S — Strong. Sole job is first-party persistence of a manual cookie-consent choice: load, save, clear,
 *     versioned schema validation. Every function is about that one concern.
 * O — Good. A schema-version bump (CURRENT_VERSION) is the built-in extension point for payload shape
 *     changes; loadManualConsent already rejects mismatched versions/malformed payloads rather than
 *     trusting them.
 * L — N/A — no inheritance hierarchy, plain functions.
 * I — Strong. Three narrow, independent functions (load/save/clear) — no caller is forced to depend on
 *     more than it needs.
 * D — Acceptable. Imports only the domain type IEntityCookieConsentCategories; no port abstracts this file (it's
 *     a standalone concrete localStorage wrapper, consumed directly by ConsentGateway) — noted plainly,
 *     consistent with other small storage wrappers in this codebase.
 */
import { IEntityCookieConsentCategories } from '../../domain/ports/IStateCookieConsent';

/**
 * First-party persistence for a manual cookie-consent choice.
 *
 * The certified CMP (Google Funding Choices) persists its own decision in the
 * `FCCDCF` cookie / TCF string, but choices made in our first-party preferences
 * modal were previously only held in memory — so "Accept all" / "Reject all"
 * were forgotten on the next load. We mirror the manual choice into localStorage
 * here and restore it on gateway init so it survives a reload.
 */
const STORAGE_KEY = 'meyer_cookie_consent';
const CURRENT_VERSION = 1;

interface PersistedConsent {
  v: number;
  categories: IEntityCookieConsentCategories;
  ts: number;
}

export function loadManualConsent(): IEntityCookieConsentCategories | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedConsent> | null;
    if (!parsed || parsed.v !== CURRENT_VERSION || !parsed.categories) return null;
    const c = parsed.categories;
    // Reject malformed payloads (e.g. an older/foreign schema) rather than trust them.
    if (typeof c.adStorage !== 'boolean' || typeof c.functionality !== 'boolean') return null;
    return {
      adStorage: !!c.adStorage,
      adUserData: !!c.adUserData,
      adPersonalization: !!c.adPersonalization,
      analyticsStorage: !!c.analyticsStorage,
      functionality: true, // strictly-necessary is always on
    };
  } catch {
    return null;
  }
}

export function saveManualConsent(categories: IEntityCookieConsentCategories): void {
  try {
    const payload: PersistedConsent = { v: CURRENT_VERSION, categories, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage unavailable / quota exceeded — degrade to in-memory only.
  }
}

export function clearManualConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
